import { createTheme, alpha } from '@mui/material/styles';

// Modern healthcare color palette
const colors = {
  primary: {
    light: '#3B82F6',
    main: '#2563EB',
    dark: '#1D4ED8',
  },
  secondary: {
    light: '#A78BFA',
    main: '#8B5CF6',
    dark: '#7C3AED',
  },
  success: {
    light: '#34D399',
    main: '#10B981',
    dark: '#059669',
  },
  error: {
    light: '#F87171',
    main: '#EF4444',
    dark: '#DC2626',
  },
  warning: {
    light: '#FBBF24',
    main: '#F59E0B',
    dark: '#D97706',
  },
  info: {
    light: '#60A5FA',
    main: '#3B82F6',
    dark: '#2563EB',
  },
};

// Softer, layered shadows (Tailwind-inspired)
const softShadows = [
  'none',
  '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  '0 25px 50px -12px rgb(0 0 0 / 0.25)',
] as const;

const getTheme = (direction: 'ltr' | 'rtl' = 'ltr') =>
  createTheme({
    direction,
    cssVariables: {
      colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    shape: {
      borderRadius: 10,
    },
    shadows: softShadows as unknown as typeof createTheme extends (options: infer O) => unknown ? O extends { shadows?: infer S } ? S : never : never,
    colorSchemes: {
      light: {
        palette: {
          primary: {
            light: colors.primary.light,
            main: colors.primary.main,
            dark: colors.primary.dark,
            contrastText: '#ffffff',
          },
          secondary: {
            light: colors.secondary.light,
            main: colors.secondary.main,
            dark: colors.secondary.dark,
            contrastText: '#ffffff',
          },
          success: {
            light: colors.success.light,
            main: colors.success.main,
            dark: colors.success.dark,
            contrastText: '#ffffff',
          },
          error: {
            light: colors.error.light,
            main: colors.error.main,
            dark: colors.error.dark,
            contrastText: '#ffffff',
          },
          warning: {
            light: colors.warning.light,
            main: colors.warning.main,
            dark: colors.warning.dark,
            contrastText: '#ffffff',
          },
          info: {
            light: colors.info.light,
            main: colors.info.main,
            dark: colors.info.dark,
            contrastText: '#ffffff',
          },
          background: {
            default: '#F8FAFC',
            paper: '#ffffff',
          },
          text: {
            primary: '#1E293B',
            secondary: '#64748B',
          },
          divider: '#E2E8F0',
          action: {
            hover: alpha('#2563EB', 0.04),
            selected: alpha('#2563EB', 0.08),
            focus: alpha('#2563EB', 0.12),
          },
        },
      },
      dark: {
        palette: {
          primary: {
            light: '#60A5FA',
            main: '#3B82F6',
            dark: '#2563EB',
            contrastText: '#ffffff',
          },
          secondary: {
            light: '#C4B5FD',
            main: '#A78BFA',
            dark: '#8B5CF6',
            contrastText: '#ffffff',
          },
          success: {
            light: '#6EE7B7',
            main: '#34D399',
            dark: '#10B981',
            contrastText: '#ffffff',
          },
          error: {
            light: '#FCA5A5',
            main: '#F87171',
            dark: '#EF4444',
            contrastText: '#ffffff',
          },
          warning: {
            light: '#FCD34D',
            main: '#FBBF24',
            dark: '#F59E0B',
            contrastText: '#1E293B',
          },
          info: {
            light: '#93C5FD',
            main: '#60A5FA',
            dark: '#3B82F6',
            contrastText: '#ffffff',
          },
          background: {
            default: '#0F172A',
            paper: '#1E293B',
          },
          text: {
            primary: '#F1F5F9',
            secondary: '#94A3B8',
          },
          divider: '#334155',
          action: {
            hover: alpha('#3B82F6', 0.08),
            selected: alpha('#3B82F6', 0.16),
            focus: alpha('#3B82F6', 0.24),
          },
        },
      },
    },
    typography: {
      fontFamily: 'Cairo, "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.01em',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.5,
      },
    },
    components: {
      // Button styling
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 20px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          contained: {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
          sizeSmall: {
            padding: '6px 14px',
            fontSize: '0.8125rem',
          },
          sizeLarge: {
            padding: '12px 28px',
            fontSize: '1rem',
          },
        },
      },
      // Fab button styling
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            },
          },
        },
      },
      // Card styling
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '20px',
            '&:last-child': {
              paddingBottom: '20px',
            },
          },
        },
      },
      // Paper styling
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 12,
          },
          elevation1: {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          },
          elevation2: {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
          elevation3: {
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
          },
        },
      },
      // TextField styling
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'box-shadow 0.2s ease-in-out',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main,
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.15)}`,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
              },
              '&.Mui-error': {
                boxShadow: `0 0 0 3px ${alpha(colors.error.main, 0.15)}`,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'box-shadow 0.2s ease-in-out',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.15)}`,
            },
            '&.Mui-error': {
              boxShadow: `0 0 0 3px ${alpha(colors.error.main, 0.15)}`,
            },
            '[data-toolpad-color-scheme="dark"] &': {
              backgroundColor: '#0F172A',
            },
          },
          notchedOutline: {
            borderColor: '#E2E8F0',
            transition: 'border-color 0.2s ease-in-out',
            '[data-toolpad-color-scheme="dark"] &': {
              borderColor: '#334155',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            '[data-toolpad-color-scheme="dark"] &': {
              color: '#94A3B8',
            },
          },
        },
      },
      // Select styling
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '[data-toolpad-color-scheme="dark"] &': {
              backgroundColor: '#0F172A',
            },
          },
        },
      },
      // Chip styling
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            transition: 'all 0.2s ease-in-out',
          },
          filled: {
            '&:hover': {
              transform: 'scale(1.02)',
            },
          },
        },
      },
      // Table styling
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: '#F8FAFC',
              borderBottom: '2px solid #E2E8F0',
              '[data-toolpad-color-scheme="dark"] &': {
                backgroundColor: '#1E293B',
                borderBottom: '2px solid #334155',
              },
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.04),
            },
            '&:nth-of-type(even)': {
              backgroundColor: alpha('#F8FAFC', 0.5),
              '[data-toolpad-color-scheme="dark"] &': {
                backgroundColor: alpha('#1E293B', 0.5),
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid #E2E8F0',
            padding: '14px 16px',
            '[data-toolpad-color-scheme="dark"] &': {
              borderBottom: '1px solid #334155',
            },
          },
        },
      },
      // Dialog styling
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.25rem',
            fontWeight: 600,
            padding: '20px 24px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '20px 24px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            gap: '12px',
          },
        },
      },
      // Alert styling
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 500,
          },
          standardSuccess: {
            backgroundColor: alpha(colors.success.main, 0.12),
            color: colors.success.dark,
          },
          standardError: {
            backgroundColor: alpha(colors.error.main, 0.12),
            color: colors.error.dark,
          },
          standardWarning: {
            backgroundColor: alpha(colors.warning.main, 0.12),
            color: colors.warning.dark,
          },
          standardInfo: {
            backgroundColor: alpha(colors.info.main, 0.12),
            color: colors.info.dark,
          },
        },
      },
      // Tooltip styling
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: '0.8125rem',
            fontWeight: 500,
            padding: '8px 12px',
          },
        },
      },
      // Accordion styling
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: '#ffffff',
            border: '1px solid #E2E8F0',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
            '[data-toolpad-color-scheme="dark"] &': {
              backgroundColor: '#0F172A',
              border: '1px solid #334155',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            minHeight: 56,
            '&.Mui-expanded': {
              minHeight: 56,
            },
            '[data-toolpad-color-scheme="dark"] &': {
              backgroundColor: '#0F172A',
            },
          },
          content: {
            '&.Mui-expanded': {
              margin: '12px 0',
            },
          },
        },
      },
      // List styling
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(colors.primary.main, 0.08),
            },
            '&.Mui-selected': {
              backgroundColor: alpha(colors.primary.main, 0.12),
              '&:hover': {
                backgroundColor: alpha(colors.primary.main, 0.16),
              },
            },
          },
        },
      },
      // Avatar styling
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      // IconButton styling
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          },
        },
      },
      // Breadcrumbs styling
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
        },
      },
      // Tab styling
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            minHeight: 48,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      // Pagination styling
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          },
        },
      },
      // Switch styling
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 46,
            height: 26,
            padding: 0,
          },
          switchBase: {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              '& + .MuiSwitch-track': {
                opacity: 1,
              },
            },
          },
          thumb: {
            width: 22,
            height: 22,
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          },
          track: {
            borderRadius: 13,
            opacity: 1,
            backgroundColor: '#E2E8F0',
          },
        },
      },
      // Skeleton styling
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          rounded: {
            borderRadius: 12,
          },
        },
      },
      // Menu styling
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '2px 6px',
            padding: '8px 12px',
            transition: 'background-color 0.15s ease-in-out',
          },
        },
      },
      // Snackbar styling
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiPaper-root': {
              borderRadius: 10,
            },
          },
        },
      },
      // Divider styling
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: '#E2E8F0',
            '[data-toolpad-color-scheme="dark"] &': {
              borderColor: '#334155',
            },
          },
        },
      },
      // Linear Progress styling
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 6,
          },
        },
      },
      // Circular Progress styling
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            strokeLinecap: 'round',
          },
        },
      },
    },
  });

export default getTheme;
